/* global process */
import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

// process.env.REACT_APP_VERSION is replaced at build time by webpack's
// DefinePlugin. The fallback "dev" lets the component be a no-op during
// development without DefinePlugin substitution.
const APP_VERSION = process.env.REACT_APP_VERSION || "dev";

const VERSION_URL = "/version.json";
// Throttle floor between version checks. Mounting, location changes,
// becoming-visible, and the periodic interval all share this floor so a
// chatty user never DDOS-checks their own server.
const CHECK_THROTTLE_MS = 5 * 60 * 1000; // 5 min
// Reload-loop guard: if we just did a versioned assign within this window
// and we're STILL stale, something is wrong upstream — fall through and
// render the (stale) app rather than ping-pong the user.
const ASSIGN_GUARD_MS = 30 * 1000;
const ASSIGN_GUARD_KEY = "oy_version_assign_v1";

function VersionGuard() {
  const location = useLocation();
  const lastCheckRef = useRef(0);
  const lastLocationRef = useRef(
    location.pathname + location.search + location.hash
  );
  const [isStale, setIsStale] = useState(false);

  // Periodic + on-mount + on-becoming-visible version probe.
  useEffect(() => {
    if (APP_VERSION === "dev") return; // no-op when not built
    let cancelled = false;

    const check = async () => {
      const now = Date.now();
      if (now - lastCheckRef.current < CHECK_THROTTLE_MS) return;
      lastCheckRef.current = now;
      try {
        const res = await fetch(VERSION_URL + "?_=" + now, {
          cache: "no-store",
          credentials: "omit",
        });
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        if (data && data.version) {
          if (data.version === APP_VERSION) {
            // Versions match — clear any stale-flag carry-over so a future
            // mismatch can flip us cleanly.
            try {
              window.sessionStorage.removeItem(ASSIGN_GUARD_KEY);
            } catch {
              /* ignore */
            }
          } else {
            setIsStale(true);
          }
        }
      } catch {
        /* network blip — ignore, retry next tick */
      }
    };

    const onVisible = () => {
      if (document.visibilityState === "visible") check();
    };

    check();
    const intervalId = setInterval(check, CHECK_THROTTLE_MS);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      cancelled = true;
      clearInterval(intervalId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // Navigation-time interception. We deliberately do NOT reload immediately
  // when isStale flips — that would yank the user out of whatever they were
  // doing. We wait until the user themselves navigates somewhere, then
  // perform a silent full-page load to that destination so they land on the
  // intended route, rendered by the latest build's index.html + chunks.
  useEffect(() => {
    const current =
      location.pathname + location.search + location.hash;
    const previous = lastLocationRef.current;
    lastLocationRef.current = current;

    if (!isStale) return;
    // No real navigation occurred (initial mount, or isStale just flipped) —
    // do not interrupt the user.
    if (current === previous) return;

    // Reload-loop guard.
    let lastAssign = 0;
    try {
      lastAssign = parseInt(
        window.sessionStorage.getItem(ASSIGN_GUARD_KEY) || "0",
        10
      );
    } catch {
      /* ignore */
    }
    if (Date.now() - lastAssign < ASSIGN_GUARD_MS) return;

    try {
      window.sessionStorage.setItem(ASSIGN_GUARD_KEY, String(Date.now()));
    } catch {
      /* ignore */
    }
    // assign() (not replace()) so the user can press Back to return to the
    // previous route they were on if they want to.
    window.location.assign(current);
  }, [location.pathname, location.search, location.hash, isStale]);

  return null;
}

export default VersionGuard;
